import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Person, DataActions, useArrivalContext} from 'src/context/ArrivalContext';
import {DropinModalPerson} from './DropinModalPerson';
import {Button} from 'src/components/form/button/Button';
import {validateEmail, validatePhone} from 'src/services/validation/validation';
import {Checkbox} from 'src/components/form/checkbox/Checkbox';
import './DropinModalPeople.scss';
import ModalPortal from 'src/components/layout/modal/ModalPortal';
import {fetchClinicById} from 'src/services/api/api';
import {ClinicJSON} from 'src/services/api/types/types';

export function DropinModalPeople() {
    const {t} = useTranslation();

    const {dispatch, dataState} = useArrivalContext();
    const people = [];
    const requireContactInfo = dataState.settings?.requireContactInfo || false;
    const [policyConsent, setPolicyConsent] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [clinic, setClinic] = useState<ClinicJSON | null>(null);

    const [persons, setPersons] = useState<Array<Person>>(() => {
        const persons = Array.from(Array(dataState.reasonState.numberPatients || 1)).map((_, index): Person => {
            const p = dataState.persons[index];

            if (p === undefined) {
                return {
                    identity: '',
                    identityValid: false,
                    givenName: '',
                    familyName: '',
                    age: undefined,
                    gender: 'unknown'
                };
            }

            return {
                identity: p.identity,
                identityValid: true,
                givenName: p.givenName,
                familyName: p.familyName,
                age: p.age ?? undefined,
                gender: p.gender ?? 'unknown'
            };
        });

        return persons;
    });

    const {minAge, maxAge} = dataState.reasonState.allowedAge || {};

    const peopleValid =
        persons
            .map((p) => {
                const phone = p.contactInfo?.mobile || '';
                const email = p.contactInfo?.email || '';
                const marketing = !requireContactInfo || p.contactInfo?.marketing !== undefined;

                return (
                    p.familyName !== '' &&
                    p.givenName !== '' &&
                    p.identityValid &&
                    marketing &&
                    (!requireContactInfo || (validatePhone(phone) && phone.length > 0)) &&
                    (!requireContactInfo || validateEmail(email) || email.length === 0)
                );
            })
            .includes(false) === false;

    const numOfPatients = dataState.reasonState.numberPatients ?? 1;
    for (let i = 0; i < numOfPatients; i++) {
        const headline = numOfPatients > 1 ? 'Person ' + (i + 1) : '';
        people.push(
            <DropinModalPerson
                key={i}
                index={i}
                person={persons[i]}
                headline={headline}
                ageRange={[minAge || null, maxAge || null]}
                onChange={(updatedPerson) => {
                    const newPersons = [...persons];
                    newPersons[i] = updatedPerson;
                    setPersons(newPersons);
                }}
                showContacts={requireContactInfo}
                onCopyContacts={
                    numOfPatients > 1
                        ? (phone, email, marketing) => {
                              const newPersons = persons.map((p) => {
                                  return {
                                      ...p,
                                      contactInfo: {
                                          mobile: phone,
                                          email: email,
                                          marketing: marketing
                                      }
                                  } as Person;
                              });
                              setPersons(newPersons);
                          }
                        : undefined
                }
            />
        );
    }

    useEffect(() => {
        if (!showModal || clinic !== null) {
            return;
        }

        const fetchClinic = async () => {
            const data = await fetchClinicById(dataState.clinicId);
            setClinic(data);
        };

        fetchClinic();
    }, [showModal, dataState.clinicId, clinic]);

    return (
        <form className='arrival-dropin-form' aria-label={t('arrival.dropin_form_aria_label')}>
            <div aria-live='polite' aria-atomic='true' className='screen-reader-only'></div>
            <span>{t('arrival.person_info_subheader')}</span>

            {people}

            <div style={{paddingTop: '30px'}}>
                <Checkbox
                    onClick={(bool) => {
                        setPolicyConsent(bool);
                    }}
                    id='policyConsent'
                    checked={policyConsent}
                    ariaLabel={`${t('confirm.policy.personal_data')} ${t('confirm.policy.personal_data_policy')}`}
                    jsx={
                        <>
                            <span>{t('confirm.policy.personal_data')}</span>
                            &nbsp;
                            <Button variant='text' className='in-text-link' onClick={() => setShowModal(true)}>
                                <span>{t('confirm.policy.personal_data_policy')}</span>
                            </Button>
                        </>
                    }
                />
            </div>
            {showModal && (
                <ModalPortal onCloseClick={() => setShowModal(false)}>
                    <div className='policy-modal'>
                        <h1 className='policy-modal__header'>{t('confirm.policy.header')}</h1>
                        <p className='policy-modal__content wrap-html'>{clinic?.termsText}</p>
                    </div>
                </ModalPortal>
            )}

            <div className='arrival-step-controllers'>
                <div>
                    <Button
                        onClick={() => {
                            dispatch({type: DataActions.SET_PEOPLE, value: persons});
                            dispatch({type: DataActions.SET_STEP, value: 'initial'});
                        }}
                    >
                        {t('arrival.back')}
                    </Button>
                </div>
                <div>
                    <Button
                        onClick={() => {
                            dispatch({type: DataActions.SET_PEOPLE, value: persons});
                            dispatch({type: DataActions.NEXT_STEP});
                        }}
                        disabled={peopleValid === false || policyConsent === false}
                    >
                        {t('arrival.next')}
                    </Button>
                </div>
            </div>
        </form>
    );
}
